import React, { useEffect, useMemo, useState } from 'react'
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { useDispatch, useSelector } from 'react-redux'
import { CoreTextField } from 'src/components/core'
import { getTagListAction } from 'src/reducers/tag/action'

const FilterPopover = ({
  anchorEl,
  open,
  onClose,
  selectedTagIds = [],
  onChange,
}) => {
  const dispatch = useDispatch()
  const allTags = useSelector(state => state.tag.list)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (open && allTags.length === 0) {
      dispatch(getTagListAction())
    }
  }, [open, allTags.length, dispatch])

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds])

  const filteredTags = useMemo(() => {
    if (!search.trim()) return allTags
    const q = search.trim().toLowerCase()
    return allTags.filter(t => (t.name || '').toLowerCase().includes(q))
  }, [allTags, search])

  const toggle = tagId => {
    const next = selectedSet.has(tagId)
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId]
    if (onChange) onChange(next)
  }

  const clearAll = () => {
    if (onChange) onChange([])
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: { width: 280, p: '12px', borderRadius: '12px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '8px',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Filter by tags
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {selectedTagIds.length > 0 && (
            <Typography
              role="button"
              onClick={clearAll}
              sx={{
                fontSize: '12px',
                color: '#3D7CCE',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Clear
            </Typography>
          )}
          <IconButton size="small" onClick={onClose}>
            <Icon icon="mdi:close" width={16} height={16} />
          </IconButton>
        </Box>
      </Box>

      <CoreTextField
        marginTop={false}
        size="small"
        fullWidth
        placeholder="Search tags"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon="mdi:search" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ maxHeight: 240, overflowY: 'auto', mt: '8px' }}>
        {filteredTags.map(tag => (
          <Box
            key={tag.id}
            onClick={() => toggle(tag.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              p: '4px',
              borderRadius: '6px',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f3f5f9' },
            }}
          >
            <Checkbox
              checked={selectedSet.has(tag.id)}
              size="small"
              onClick={e => {
                e.stopPropagation()
                toggle(tag.id)
              }}
              sx={{ p: 0 }}
            />
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: tag.color,
                display: 'inline-block',
              }}
            />
            <Typography sx={{ fontSize: '14px' }}>{tag.name}</Typography>
          </Box>
        ))}
        {filteredTags.length === 0 && (
          <Typography
            sx={{
              fontSize: '13px',
              color: '#9aa3b2',
              textAlign: 'center',
              py: '12px',
            }}
          >
            {search ? 'No tags match your search.' : 'No tags yet.'}
          </Typography>
        )}
      </Box>
    </Popover>
  )
}

export default FilterPopover
